/* Multi-select dropdown for US states (GMB Service Areas).
   Shared by the publisher onboarding form and the offer-application flow. */
import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";

export const MultiStateSelect = ({
  options,
  values,
  onChange,
  placeholder = "Select states…",
  dark = false,
  testid,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const toggle = (opt) =>
    onChange(values.includes(opt) ? values.filter((v) => v !== opt) : [...values, opt]);

  const btnCls = dark
    ? "w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2.5 text-sm text-left flex items-center justify-between focus:border-blue-400 focus:outline-none"
    : "w-full px-4 py-3 border border-slate-200 rounded-xl text-left flex items-center justify-between focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white";
  const panelCls = dark
    ? "absolute z-30 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-slate-700 bg-slate-800 shadow-xl"
    : "absolute z-30 mt-1 w-full max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl";

  return (
    <div className="relative" ref={ref} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={btnCls}
        data-testid={`${testid}-toggle`}
      >
        <span className={values.length ? (dark ? "text-slate-100" : "text-slate-800") : "text-slate-400"}>
          {values.length ? `${values.length} state${values.length > 1 ? "s" : ""} selected` : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""} ${dark ? "text-slate-400" : "text-slate-500"}`} />
      </button>

      {open && (
        <div className={panelCls} data-testid={`${testid}-panel`}>
          {options.map((opt) => {
            const checked = values.includes(opt);
            return (
              <button
                type="button"
                key={opt}
                onClick={() => toggle(opt)}
                data-testid={`${testid}-opt-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${
                  dark
                    ? `hover:bg-slate-700 ${checked ? "text-blue-300" : "text-slate-200"}`
                    : `hover:bg-slate-50 ${checked ? "text-blue-700" : "text-slate-700"}`
                }`}
              >
                <span
                  className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                    checked
                      ? "bg-blue-600 border-blue-600"
                      : dark
                        ? "border-slate-500"
                        : "border-slate-300"
                  }`}
                >
                  {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                </span>
                {opt}
              </button>
            );
          })}
        </div>
      )}

      {values.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`${testid}-chips`}>
          {values.map((v) => (
            <span
              key={v}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                dark ? "bg-blue-500/15 text-blue-300 border border-blue-500/30" : "bg-blue-50 text-blue-700 border border-blue-200"
              }`}
            >
              {v}
              <button
                type="button"
                onClick={() => toggle(v)}
                className="hover:opacity-70"
                data-testid={`${testid}-chip-remove-${v.toLowerCase().replace(/\s+/g, "-")}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
